import React, {useEffect} from 'react';

import {useStore} from 'effector-react';
import {HelmetProvider} from 'react-helmet-async';
import {Redirect, Route, Router, Switch} from 'react-router-dom';

import {Content} from 'components/common/Content';
import {Header} from 'components/common/Header';
import {PrivateRoute} from 'components/common/PrivateRoute';
import {MainSidebar} from 'components/common/Sidebar/MainSidebar';
import {MobileSidebar} from 'components/common/Sidebar/MobileSidebar';
import {NotFound} from 'components/pages/NotFound';
import {ErrorBoundary} from 'components/ui-kit/ErrorBoundary';
import {authBroadcast} from 'core/broadcastAuth';
import {history} from 'core/history';
import {PUBLIC_ROUTES_DATA, ROUTE, ROUTES_DATA} from 'core/routes';
import {isAuth$, isCustomsRole$} from 'models/auth';

import {Background, GlobalStyle, ToastContainerRoot} from './styled';

export function App() {
    const isCustomsRole = useStore(isCustomsRole$);
    const isAuth = useStore(isAuth$);

    const redirectMapping: {DEFAULT: ROUTE; CUSTOMS: ROUTE} = {
        DEFAULT: ROUTE.DASHBOARD,
        CUSTOMS: ROUTE.DECLARATIONS,
    };

    const role = isCustomsRole ? 'CUSTOMS' : 'DEFAULT';
    const redirect = redirectMapping[role] ?? redirectMapping.DEFAULT;

    useEffect(() => () => authBroadcast.close(), []);

    return (
        <>
            <GlobalStyle />
            <Router history={history}>
                <HelmetProvider>
                    <ErrorBoundary>
                        {isAuth && <Header />}
                        {isAuth && <MobileSidebar />}
                        {isAuth && <MainSidebar />}
                        <Switch>
                            <Route path="/">
                                <ToastContainerRoot
                                    position="bottom-right"
                                    autoClose={5000}
                                    hideProgressBar
                                    closeOnClick
                                />
                                <Switch>
                                    {Object.entries(PUBLIC_ROUTES_DATA).map(([path, route]) => (
                                        <Route path={path} exact={route.exact} key={path}>
                                            {route.component}
                                        </Route>
                                    ))}
                                    {Object.entries(ROUTES_DATA).map(
                                        ([path, data]) =>
                                            data && (
                                                <PrivateRoute
                                                    key={path}
                                                    path={path}
                                                    exact={data.exact}
                                                    permissions={data.permissions}
                                                    permissionAccessRoles={
                                                        data.permissionAccessRoles
                                                    }
                                                    roles={data.roles}
                                                >
                                                    <Content flex={data.flex}>
                                                        {data.component}
                                                    </Content>
                                                </PrivateRoute>
                                            ),
                                    )}
                                    <Route path="/" exact>
                                        <Redirect to={redirect} />
                                    </Route>
                                    <Route path="*" exact>
                                        <Content>
                                            <NotFound />
                                        </Content>
                                    </Route>
                                </Switch>
                            </Route>
                        </Switch>
                    </ErrorBoundary>
                </HelmetProvider>
            </Router>
            <Background />
        </>
    );
}
